import React, { useEffect, useState } from "react";
import { PhotoProvider, PhotoView } from "react-photo-view";
import "react-photo-view/dist/react-photo-view.css";
import { getInitials } from "../../utils/helpers/getInitials";
import { resolveAssetUrl } from "../../utils/helpers/storeSetting";
import "./style.css";

const sizeClassMap = {
  xs: "avatar-xs",
  sm: "avatar-sm",
  md: "avatar-md",
  lg: "avatar-lg",
  xl: "avatar-xl",
};

const AvatarMedia = ({ src, alt, initials, preview = false }) => {
  const [imageFailed, setImageFailed] = useState(false);

  useEffect(() => setImageFailed(false), [src]);

  const hasImage = Boolean(src) && !imageFailed;
  const image = hasImage ? (
    <img
      src={src}
      alt={alt || "Avatar"}
      className="akas-user-avatar__image"
      loading="lazy"
      decoding="async"
      onError={() => setImageFailed(true)}
    />
  ) : (
    <span className="akas-user-avatar__initials" aria-hidden="true">
      {initials || "A"}
    </span>
  );

  return preview && hasImage ? <PhotoView src={src}>{image}</PhotoView> : image;
};

const UserAvatar = ({
  fullName,
  imageUrl,
  size = "sm",
  className = "",
  justPhotoView = false,
  isMultiple = false,
  datas = [],
}) => {
  const imageSrc = resolveAssetUrl(imageUrl, "");
  const initials = getInitials(fullName || "Akas");
  const sizeClass = sizeClassMap[size] || "avatar-md";

  if (isMultiple && datas.length > 0) {
    return (
      <PhotoProvider
        maskOpacity={0.756}
        toolbarRender={({ onScale, scale, rotate, onRotate }) => (
          <div className="d-flex gap-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-rotate-ccw"
              onClick={() => onRotate(rotate - 90)}
              role="button"
              aria-label="Putar gambar ke kiri">
              <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
              <path d="M3 3v5h5" />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-rotate-cw"
              onClick={() => onRotate(rotate + 90)}
              role="button"
              aria-label="Putar gambar ke kanan">
              <path d="M21 12a9 9 0 1 1-9-9c2.52 0 4.93 1 6.74 2.74L21 8" />
              <path d="M21 3v5h-5" />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-zoom-in"
              onClick={() => onScale(scale + 1)}
              role="button"
              aria-label="Perbesar gambar">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="11" x2="11" y1="8" y2="14" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="lucide lucide-zoom-out"
              onClick={() => onScale(scale - 1)}
              role="button"
              aria-label="Perkecil gambar">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" x2="16.65" y1="21" y2="16.65" />
              <line x1="8" x2="14" y1="11" y2="11" />
            </svg>
          </div>
        )}
        overlayRender={({ index }) => (
          <div className="akas-user-avatar__viewer-caption" key={index}>
            <span className="fw-bold mb-0">
              {datas[index]?.image_path ? datas[index].image_path.split("/").pop() : ""}
              {datas[index]?.is_main ? <span className="badge bg-success text-white ms-2">Utama</span> : null}
            </span>
            <p className="mb-0">{fullName}</p>
          </div>
        )}>
        {datas.map((data, index) => {
          const dataName = data.fullName || data.name || fullName || "Akas";
          const dataImageUrl = resolveAssetUrl(data.image_path || data.image || data.image_url, "");

          return (
            <span
              key={data.id || data.image_path || index}
              className={`avatar akas-item-avatar is-rounded ${dataImageUrl ? "has-image" : "is-fallback"} ${className}`.trim()}
              title={dataName}
              aria-label={`Avatar ${dataName}`}>
              <AvatarMedia src={dataImageUrl} alt={dataName} initials={getInitials(dataName)} preview />
            </span>
          );
        })}
      </PhotoProvider>
    );
  }

  return (
    <PhotoProvider bannerVisible={false} maskOpacity={0.6}>
      <span
        className={`avatar ${sizeClass} akas-item-avatar is-rounded ${imageSrc ? "has-image" : "is-fallback"} ${className}`.trim()}
        title={fullName || "Akas"}
        aria-label={`Avatar ${fullName || "Akas"}`}>
        <AvatarMedia src={imageSrc} alt={fullName} initials={initials} preview={!justPhotoView} />
      </span>
    </PhotoProvider>
  );
};

export default UserAvatar;
